import type {
  ResolutionListFilters,
  ResolutionsListFiltersContext
} from '@library/types/resolutions/resolutionsList';
import {
  defaultResolutionListFilters,
  resolutionStatusOptions
} from '@bo-constants/resolutions/resolutionsList';
import { useCallback, useMemo, useState } from 'react';
import type { DateRange } from '@library/types/dates';
import type { SelectOptions } from '@library/types/utils';
import { useGetAdminUsersQuery } from '@bo-services/userApi';
import { useGetRolesQuery } from '@bo-services/rolesApi';

export const useResolutionsList = () => {
  const [resolutionListFilters, setResolutionListFilters] =
    useState<ResolutionListFilters>(defaultResolutionListFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { data: rolesData } = useGetRolesQuery({});

  const resolutionTeam = rolesData?.data.find(
    (role) => role.attributes.name === 'Resolution Team'
  );
  const resolutionTeamId = resolutionTeam?.id;

  const { data: userData } = useGetAdminUsersQuery({
    role_ids: [resolutionTeamId ?? '']
  });

  const adminUserOptions: SelectOptions = useMemo(() => {
    if (!userData?.users) return [];
    return userData.users.map(({ id, first_name, last_name }) => ({
      key: id,
      name: `${first_name} ${last_name}`,
      value: id
    }));
  }, [userData?.users]);

  const handleCurrentPageChange = useCallback(() => {
    setCurrentPage((prevPage) => prevPage + 1);
  }, []);

  const handleResetCurrentPage = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [currentPage]);

  const handleAdminUserChange = useCallback(
    (value: ResolutionListFilters['adminUsers']) => {
      handleResetCurrentPage();
      setResolutionListFilters((prevFilters) => ({
        ...prevFilters,
        adminUsers: value || []
      }));
    },
    [handleResetCurrentPage, setResolutionListFilters]
  );

  const handleCompletedOnChange = useCallback(
    (value: DateRange) => {
      const { fromDate: completedOnFromDate, toDate: completedOnToDate } =
        value;
      handleResetCurrentPage();
      setResolutionListFilters((prevFilters) => ({
        ...prevFilters,
        completedOnFromDate,
        completedOnToDate
      }));
    },
    [handleResetCurrentPage, setResolutionListFilters]
  );

  const handleCreatedAtChange = useCallback(
    (value: DateRange) => {
      const { fromDate: createdAtFromDate, toDate: createdAtToDate } = value;
      handleResetCurrentPage();
      setResolutionListFilters((prevFilters) => ({
        ...prevFilters,
        createdAtFromDate,
        createdAtToDate
      }));
    },
    [handleResetCurrentPage, setResolutionListFilters]
  );

  const handleResolutionStatusChange = useCallback(
    (value: ResolutionListFilters['resolutionStatuses']) => {
      handleResetCurrentPage();
      setResolutionListFilters((prevFilters) => ({
        ...prevFilters,
        resolutionStatuses: value || []
      }));
    },
    [handleResetCurrentPage, setResolutionListFilters]
  );

  const handleResetFilters = useCallback(() => {
    handleResetCurrentPage();
    setResolutionListFilters(defaultResolutionListFilters);
  }, [handleResetCurrentPage, setResolutionListFilters]);

  const resolutionsListFiltersContext: ResolutionsListFiltersContext = useMemo(
    () => ({
      adminUserOptions,
      currentPage,
      onAdminUserChange: handleAdminUserChange,
      onCompletedOnChange: handleCompletedOnChange,
      onCreatedAtChange: handleCreatedAtChange,
      onCurrentPageChange: handleCurrentPageChange,
      onResetCurrentPageChange: handleResetCurrentPage,
      onResetFilters: handleResetFilters,
      onResolutionStatusChange: handleResolutionStatusChange,
      resolutionListFilters,
      resolutionStatusOptions
    }),
    [
      adminUserOptions,
      currentPage,
      handleAdminUserChange,
      handleCompletedOnChange,
      handleCreatedAtChange,
      handleCurrentPageChange,
      handleResetCurrentPage,
      handleResetFilters,
      handleResolutionStatusChange,
      resolutionListFilters
    ]
  );

  return {
    resolutionsListFiltersContext
  };
};
